import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade } from 'swiper/modules';
import { PencilIcon } from '@heroicons/react/24/solid';

import { STYLES, BOOKING_METHOD_OPTIONS, TRIAL_MESSAGE } from '../constants.js';
import { getCountryNameByCode } from '../utils/helpers.js';
import { StepButton } from './StepButton.js';
import { OptionButton } from './OptionButton.js';
import { CategoryButton } from './CategoryButton.js';
import { PasswordRules } from './PasswordRules.js';
import { InputField } from './InputField.js';
import { Checkbox } from './Checkbox.js';
import { SelectField } from './SelectField.js';
import { ValidationErrors } from './ValidationErrors.js';
import { ApiError } from './ApiError.js';
import { LoadingSlide } from './LoadingSlide.js';
import { FieldError } from './FieldError.js';
import ReCaptchaBox from '../../../../components/ReCaptchaBox.js';

export function SignupFormPanel({
  activeIndex,
  isMobileTablet,
  swiperRef,
  setActiveIndex,
  handlePrevious,
  formOptions,
  category,
  handleCategorySelect,
  teamSize,
  handleTeamSizeSelect,
  bookingMethod,
  handleBookingMethodSelect,
  otherSoftware,
  handleOtherSoftwareChange,
  referralId,
  showPromoCode,
  handleShowPromoCode,
  showRegionFields,
  state,
  hasFieldError,
  getFieldError,
  handleFieldChange,
  password,
  passwordRules,
  hasTerms,
  showConsentFields,
  marketingConsent,
  getGeneralValidationErrors,
  handleRecaptchaToken,
  apiError,
  handleStartTrial,
  isMutating,
  handleShowRegionFields,
  formRef,
  formSuccess,
  isFullPageMode,
}) {
  const trialMessage = isFullPageMode
    ? 'Set up in minutes, save hours.\nNo credit card required.'
    : 'Enjoy your free 14-day trial.\nNo credit card required.\nNo obligations or fixed-term contracts.';
  const fullPageSlideContentClass = isFullPageMode
    ? 'min-h-full flex flex-col'
    : '';
  // Full-page loading step: panel fills the viewport under the 64px fixed header (all breakpoints) and centres its content
  const centreLoadingStep = isFullPageMode && activeIndex === 4;

  return (
    <div
      className={`${isFullPageMode ? 'w-full lg:w-1/2 min-h-0 lg:h-[calc(100dvh-64px)] lg:overflow-y-auto lg:overscroll-contain' : 'w-full xl:w-1/2 min-h-[550px]'} ${centreLoadingStep ? 'h-[calc(100dvh-64px)] flex flex-col justify-center' : ''}`}
    >
      <div
        className={`${isFullPageMode && !centreLoadingStep ? 'xl:min-h-full' : ''}`}
      >
        <Swiper
          modules={[EffectFade]}
          effect="fade"
          autoHeight={false}
          speed={isMobileTablet ? 0 : 300}
          slidesPerView={1}
          className={`swiper-signup w-full max-w-[39rem] ${isFullPageMode ? 'mx-auto' : 'h-full'}`}
          onSwiper={(swiper) => {
            swiperRef.current = { swiper };
          }}
          allowTouchMove={false}
          onActiveIndexChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        >
          {/* Step 1: Category Selection */}
          <SwiperSlide
            className={`${isFullPageMode ? 'py-[60px] md:py-[150px] px-6 md:px-12' : 'px-6 md:px-12 py-12 md:pt-16 lg:pt-20'} ${STYLES.swiperSlide} ${activeIndex === 0 ? '' : 'max-h-0 !opacity-0 overflow-hidden'}`}
          >
            <div className={fullPageSlideContentClass}>
              <StepButton step={1} onClick={handlePrevious} />
              {isFullPageMode ? (
                <>
                  <h3 className={STYLES.formStepTitle}>
                    Start your free 14 day trial
                  </h3>
                  <p className="py-2 text-sm whitespace-pre-line text-charcoal-800 font-medium">
                    What describes you best?
                  </p>
                </>
              ) : (
                <h3 className={STYLES.formStepTitle}>
                  What describes you best?
                </h3>
              )}
              <div className="flex flex-wrap gap-2.5 mb-4">
                {formOptions.categories.map(([key, option]) => (
                  <CategoryButton
                    key={key}
                    option={option}
                    selected={category === option}
                    onClick={() => handleCategorySelect(option)}
                  />
                ))}
              </div>
              <p className="mt-4 text-sm whitespace-pre-line text-charcoal-900">
                {trialMessage}
              </p>
            </div>
          </SwiperSlide>

          {/* Step 2: Team Size Selection */}
          <SwiperSlide
            className={`${isFullPageMode ? 'py-[60px] md:py-[150px] px-6 md:px-12' : 'px-6 md:px-12 py-12 md:pt-16 lg:pt-20'} ${STYLES.swiperSlide} ${activeIndex === 1 ? '' : 'max-h-0 !opacity-0 overflow-hidden'}`}
          >
            <div className={fullPageSlideContentClass}>
              <StepButton step={2} onClick={handlePrevious} />
              <h3 className={STYLES.formStepTitle}>
                How many team members will take bookings in Timely?
              </h3>
              <div className="flex flex-col gap-2.5 mb-4">
                {formOptions.sizes.map(([key, option]) => (
                  <OptionButton
                    key={key}
                    option={option}
                    selected={teamSize === option}
                    onClick={() => handleTeamSizeSelect(option)}
                  />
                ))}
              </div>
              {!isFullPageMode && (
                <p className="mt-4 text-sm whitespace-pre-line">
                  {trialMessage}
                </p>
              )}
            </div>
          </SwiperSlide>

          {/* Step 3: Booking Method Selection */}
          <SwiperSlide
            className={`${isFullPageMode ? 'py-[60px] md:py-[150px] px-6 md:px-12' : 'px-6 md:px-12 py-12 md:pt-16 lg:pt-20'} ${STYLES.swiperSlide} ${activeIndex === 2 ? '' : 'max-h-0 !opacity-0 overflow-hidden'}`}
          >
            <div className={fullPageSlideContentClass}>
              <StepButton step={3} onClick={handlePrevious} />
              <h3 className={STYLES.formStepTitle}>
                How do you currently manage your bookings?
              </h3>
              <div className="flex flex-col gap-2.5 mb-4">
                {BOOKING_METHOD_OPTIONS.map((option) =>
                  option === 'Other software' ? (
                    <OptionButton
                      key={option}
                      option={option}
                      selected={bookingMethod === option}
                      onClick={() => handleBookingMethodSelect(option)}
                    >
                      {bookingMethod === 'Other software' && (
                        <label className="border-0 rounded-lg w-full bg-charcoal-100 p-2.5 focus-within:outline-charcoal-700 focus-within:outline-2 focus-within:outline">
                          <select
                            name="otherSoftware"
                            className="w-full bg-transparent outline-none"
                            value={otherSoftware}
                            onChange={handleOtherSoftwareChange}
                          >
                            <option value="" disabled>
                              Which software provider?
                            </option>
                            {formOptions.previousSystems.map(([key, value]) => (
                              <option key={key} value={value}>
                                {value}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </OptionButton>
                  ) : (
                    <OptionButton
                      key={option}
                      option={option}
                      selected={bookingMethod === option}
                      onClick={() => handleBookingMethodSelect(option)}
                    />
                  ),
                )}
              </div>
              {!isFullPageMode && (
                <p className="mt-4 text-sm whitespace-pre-line">
                  {trialMessage}
                </p>
              )}
            </div>
          </SwiperSlide>

          {/* Step 4: User Details Form */}
          <SwiperSlide
            className={`${isFullPageMode ? 'py-[60px] md:py-[150px] lg:py-[60px] xl:py-[80px] 3xl:py-[150px] px-6 md:px-12' : 'px-6 md:px-12 py-12 md:pt-16 lg:pt-12'} ${STYLES.swiperSlide} ${activeIndex === 3 ? '' : 'max-h-0 !opacity-0 overflow-hidden'}`}
          >
            <div className={fullPageSlideContentClass}>
              <StepButton step={4} onClick={handlePrevious} />
              <h3 className={STYLES.formStepTitle}>
                Nice, let's get some details
              </h3>
              <form ref={formRef} className="my-2 flex flex-col gap-2.5">
                {/* Name Fields */}
                <div className="flex flex-col md:flex-row gap-2.5">
                  <div className="w-full md:w-1/2 flex flex-col gap-2.5">
                    <InputField
                      name="firstName"
                      autoComplete="given-name"
                      placeholder="First name"
                      hasError={hasFieldError('first name')}
                      error={getFieldError('first name')}
                      onChange={handleFieldChange}
                    />
                  </div>
                  <div className="w-full md:w-1/2 flex flex-col gap-2.5">
                    <InputField
                      name="lastName"
                      autoComplete="family-name"
                      placeholder="Last name"
                      hasError={hasFieldError('last name')}
                      error={getFieldError('last name')}
                      onChange={handleFieldChange}
                    />
                  </div>
                </div>

                {/* Business and Contact Fields */}
                <InputField
                  name="businessName"
                  placeholder="Business name"
                  hasError={hasFieldError('business name')}
                  error={getFieldError('business name')}
                  onChange={handleFieldChange}
                />
                <InputField
                  name="phoneNumber"
                  autoComplete="tel"
                  placeholder="Phone number"
                  hasError={hasFieldError('phone number')}
                  error={getFieldError('phone number')}
                  onChange={handleFieldChange}
                />
                <InputField
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  hasError={hasFieldError('email')}
                  error={getFieldError('email')}
                  onChange={handleFieldChange}
                />

                {/* Password Field */}
                <InputField
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Password"
                  hasError={hasFieldError('password')}
                  error={getFieldError('password')}
                  onChange={handleFieldChange}
                />
                <PasswordRules value={password} rules={passwordRules} />

                {/* Promo Code Field */}
                {referralId === '' && (
                  <>
                    {!showPromoCode && (
                      <div>
                        <button
                          type="button"
                          className="text-midnight-700 hover:text-midnight-600 transition-colors"
                          onClick={handleShowPromoCode}
                        >
                          Have a promo code?
                        </button>
                      </div>
                    )}

                    {showPromoCode && (
                      <InputField
                        name="promoCode"
                        placeholder="Promo code"
                        hasError={hasFieldError('promo code')}
                        error={getFieldError('promo code')}
                        onChange={handleFieldChange}
                      />
                    )}
                  </>
                )}

                {/* Regional Fields */}
                {showRegionFields && (
                  <>
                    <SelectField
                      name="countryCode"
                      value={state.countryCode || ''}
                      onChange={handleFieldChange}
                      options={formOptions.countries}
                      placeholder="Country"
                      hasError={
                        hasFieldError('countryCode') ||
                        hasFieldError('currency') ||
                        hasFieldError('timeZone')
                      }
                      optionValue="country_code"
                      optionLabel="country_name"
                    />
                    <SelectField
                      name="currency"
                      value={state.currency || ''}
                      onChange={handleFieldChange}
                      options={formOptions.currencies}
                      placeholder="Currency"
                      hasError={
                        hasFieldError('countryCode') ||
                        hasFieldError('currency') ||
                        hasFieldError('timeZone')
                      }
                      optionValue="Currency_code"
                      optionLabel="Currency_long_name"
                    />
                    <SelectField
                      name="timeZone"
                      value={state.timeZone || ''}
                      onChange={handleFieldChange}
                      options={formOptions.timeZones}
                      placeholder="Time zone"
                      hasError={
                        hasFieldError('countryCode') ||
                        hasFieldError('currency') ||
                        hasFieldError('timeZone')
                      }
                    />

                    {(getFieldError('countryCode') ||
                      getFieldError('currency') ||
                      getFieldError('timeZone')) && (
                      <FieldError
                        error={
                          getFieldError('countryCode') ||
                          getFieldError('currency') ||
                          getFieldError('timeZone')
                        }
                      />
                    )}
                  </>
                )}

                {/* Terms Checkbox */}
                <Checkbox
                  checked={hasTerms}
                  onChange={handleFieldChange}
                  hasError={hasFieldError('hasTerms')}
                  error={getFieldError('hasTerms')}
                >
                  <span>
                    I agree to the{' '}
                    <a
                      href="/terms-of-service/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-midnight-700"
                    >
                      terms and conditions
                    </a>
                  </span>
                </Checkbox>

                {/* Marketing Consent Field */}
                {showConsentFields && (
                  <Checkbox
                    name="marketingConsentGranted"
                    checked={state.marketingConsentGranted}
                    onChange={handleFieldChange}
                  >
                    <span>
                      {marketingConsent.marketingConsentText}
                      <span className="text-charcoal-700"> * (optional)</span>
                    </span>
                  </Checkbox>
                )}

                <ValidationErrors errors={getGeneralValidationErrors()} />

                {/* reCAPTCHA Placeholder */}
                <div className="my-2">
                  <ReCaptchaBox
                    siteKey={
                      window.location.host === 'www.gettimely.com'
                        ? '6LfPOokaAAAAAKrEHC8PAERYfZ-sAeCL4TGBQCXf'
                        : '6Lf064gaAAAAAMoXryevYaOHh7nywZqQZIjD9gUN'
                    }
                    onToken={handleRecaptchaToken}
                    action="signup"
                  />
                  {hasFieldError('reCAPTCHA') && (
                    <FieldError error={getFieldError('reCAPTCHA')} />
                  )}
                </div>

                <ApiError error={apiError} />

                {/* Submit Button */}
                <button
                  type="button"
                  className="p-3 rounded-full text-charcoal-900 bg-rose-300 transition [@media(hover:hover)]:[@media(pointer:fine)]:hover:shadow-button [@media(hover:hover)]:[@media(pointer:fine)]:hover:bg-rose-100 [@media(hover:hover)]:[@media(pointer:fine)]:hover:-translate-y-[4px] [@media(hover:hover)]:[@media(pointer:fine)]:hover:-translate-x-[4px] [@media(hover:hover)]:[@media(pointer:fine)]:hover:active:shadow-none [@media(hover:hover)]:[@media(pointer:fine)]:hover:active:bg-rose-500 [@media(hover:hover)]:[@media(pointer:fine)]:hover:active:-translate-y-[0px] [@media(hover:hover)]:[@media(pointer:fine)]:hover:active:-translate-x-[0px]"
                  onClick={handleStartTrial}
                  disabled={isMutating}
                >
                  Start your trial
                </button>

                {/* Marketing Consent Field */}
                {showConsentFields && (
                  <span className="italic text-charcoal-700 text-sm">
                    *Consent is not a condition of purchase. Message and data
                    rates may apply. Message frequency varies. Reply STOP to opt
                    out of messages or{' '}
                    <a
                      href="https://help.gettimely.com/hc/en-gb/requests/new"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-midnight-700"
                    >
                      contact us
                    </a>{' '}
                    for assistance.
                  </span>
                )}

                {/* Edit Region Button */}
                {!showRegionFields && (
                  <button
                    type="button"
                    className="mt-2 flex items-start text-left gap-2"
                    onClick={handleShowRegionFields}
                  >
                    <PencilIcon className="size-3 text-midnight-700 translate-y-2 shrink-0" />
                    <span>
                      Edit{' '}
                      {state.countryCode && (
                        <span className="text-midnight-700">
                          {getCountryNameByCode(
                            state.countryCode,
                            formOptions.countries,
                          )}
                        </span>
                      )}{' '}
                      {state.currency && (
                        <span className="text-midnight-700">{`(${state.currency})`}</span>
                      )}
                      {(state.countryCode || state.currency) &&
                        formOptions.rawData.time_zones?.[state.timeZone] &&
                        ' and '}
                      {formOptions.rawData.time_zones?.[state.timeZone] && (
                        <span className="text-midnight-700">
                          {formOptions.rawData.time_zones[state.timeZone]} Time.
                        </span>
                      )}
                    </span>
                  </button>
                )}
              </form>
            </div>
          </SwiperSlide>

          {/* Step 5: Loading/Success */}
          <SwiperSlide
            className={`${isFullPageMode ? 'px-6 md:px-12' : 'px-6 md:px-12 py-12 md:pt-16 lg:pt-20'} items-center space-y-4 ${STYLES.swiperSlide} ${activeIndex === 4 ? '' : 'max-h-0 !opacity-0 overflow-hidden'}`}
          >
            <div className={fullPageSlideContentClass}>
              {(isMutating || formSuccess) && (
                <LoadingSlide isFullPageMode={isFullPageMode} />
              )}
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  );
}
